<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'PaymentsView',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    parameters: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getItems = (...args) => {
    if (props.parameters) {
      return $fn.value.fetch('list_payment', { parameters: props.parameters });
    }

    const parameters = params.value;
    return parameters && parameters.account && parameters.date_from
      ? $fn.value.fetch('list_payment', { parameters })
      : [];
  };
  const getInvoiceBadgeClass = (...args) => {
    const data = args[0];

    if (_.isEmpty(data)) {
      return;
    }

    const payment_balance = data.payment_balance || 0;
    const invoice_gross_amount = data.invoice_gross_amount;

    if (payment_balance == 0) {
      return 'bg-green-200';
    }

    if (payment_balance < 0) {
      return 'bg-yellow-200';
    }

    if (payment_balance < invoice_gross_amount) {
      return 'bg-blue-200';
    }
  };

  const selectedItems = ref([]);
  const params = ref({});
  const totals = ref([
    {
      id: 0,
      amount_in: 0,
      amount_out: 0,
    },
  ]);
  const paymentMethods = ref(null);
  const ds = ref(null);
  const ux = ref([]);
  const b_2248226175642056_showColumnPicker = computed(() => false);
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: false,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'payment_method',
      name: $fn.value.pgettext('PaymentBrowserView', 'Method'),
      formatter: {
        name: 'gettext',
        context: 'PaymentMethod',
        conversion: ['Unsnake', 'Sentence'],
        filterInput: true,
      },
    },
    {
      field: 'transaction_number',
      name: $fn.value.pgettext('PaymentBrowserView', 'Number'),
      type: 'command',
      formatter: [
        {
          name: 'chain',
          formatters: [
            {
              name: 'link',
              href: '#',
            },
            {
              name: 'cellclass',
              value: (row) => ({
                'fw-bold': true,
                'bg-yellow-200': !row.locked,
              }),
            },
          ],
        },
      ],
    },
    {
      field: 'transaction_type',
      name: $fn.value.pgettext('PaymentBrowserView', 'Type'),
      formatter: {
        name: 'gettext',
        context: 'TransactionType',
        conversion: 'Unsnake',
        filterInput: true,
      },
    },
    {
      field: 'transaction_date',
      name: $fn.value.pgettext('PaymentBrowserView', 'Date'),
      type: 'date',
    },
    {
      field: 'transaction_title',
      name: $fn.value.pgettext('PaymentBrowserView', 'Title'),
    },
    {
      field: 'description',
      name: $fn.value.pgettext('PaymentBrowserView', 'Description'),
    },
    {
      field: 'notes',
      name: $fn.value.pgettext('PaymentBrowserView', 'Notes'),
    },
    {
      field: 'opening_balance',
      name: $fn.value.pgettext('PaymentBrowserView', 'Opening balance'),
      type: 'number',
      formatter: 'number',
      noFooterTotals: true,
    },
    {
      field: 'amount_in',
      name: $fn.value.pgettext('PaymentBrowserView', 'In'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'amount_out',
      name: $fn.value.pgettext('PaymentBrowserView', 'Out'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'closing_balance',
      name: $fn.value.pgettext('PaymentBrowserView', 'Closing balance'),
      type: 'number',
      formatter: 'number',
      noFooterTotals: true,
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('PaymentBrowserView', 'Currency'),
    },
    {
      field: 'exchange_rate',
      name: $fn.value.pgettext('PaymentBrowserView', 'Exchange Rate'),
      type: 'number',
      formatter: 'number',
      noFooterTotals: true,
    },
    {
      field: 'reference_number',
      name: $fn.value.pgettext('PaymentBrowserView', 'Ref Nr.'),
    },
    {
      field: 'file_number',
      name: $fn.value.pgettext('PaymentBrowserView', 'File Nr.'),
    },
    {
      field: 'company_name',
      name: $fn.value.pgettext('PaymentBrowserView', 'Own company'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'company_id',
        },
      },
    },
    {
      field: 'client_name',
      name: $fn.value.pgettext('PaymentBrowserView', 'Client'),
      formatter: {
        name: 'routeLink',
        to: 'company',
        navigateOnIconClick: true,
        params: {
          id: 'client_id',
        },
      },
    },
    {
      field: 'invoices',
      name: $fn.value.pgettext('PaymentBrowserView', 'Invoices'),
      formatter: [
        {
          name: 'chain',
          separator: ', ',
          formatterContext: 'value',
          formatters: [
            {
              name: 'getvalue',
              opts: {
                field: 'invoice_number',
                source: 'value',
              },
            },
            {
              name: 'routeLink',
              to: 'invoice',
              navigateOnIconClick: true,
              params: {
                id: 'id',
              },
            },
            {
              name: 'badge',
              badgeClass: getInvoiceBadgeClass,
            },
          ],
        },
      ],
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    params: params.value,
    totals: totals.value,
    paymentMethods: paymentMethods.value,
    ds: ds.value,
    ux: ux.value,
    b_2248226175642056_showColumnPicker:
      b_2248226175642056_showColumnPicker.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_paymentMethods = () => {
    return $fn.value
      .fetch('list_payment_method')
      .then((data) =>
        _.reduce(data, (m, v, k) =>
          _.set(m, v.id, $fn.value.pgettext('PaymentMethod', v.name))
        )
      );
  };
  const $$load_ds = () => {
    return () => getItems();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'params' },
      { name: 'totals' },
      { name: 'paymentMethods' },
      { name: 'ds' },
      { name: 'ux' },
      {
        name: 'b_2248226175642056_showColumnPicker',
        nodeId: '2248226175642056',
      },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { paymentMethods: $$load_paymentMethods, ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      params,
      totals,
      paymentMethods,
      ds,
      ux,
      b_2248226175642056_showColumnPicker,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.parameters,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parameters');
    }
  );

  const act_2248226175642056_setData_bu877xvqz_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_showModal_38sysaxdq_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        modal: true,
        oid: $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_hhfq7zhxx_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'transaction_number';
  };

  const act_7315092382398562_reloadComponentData_b45l939l1 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_o7hk2aqp9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_b45l939l1,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_o7hk2aqp9],
    event: 'reload',
  });
  const act_2248226175642056_setData_bu877xvqz = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_bu877xvqz_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_xq8h3bw2m =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_bu877xvqz,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_xq8h3bw2m],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_showModal_38sysaxdq =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_38sysaxdq_getActionArgs,
    });
  const inst_evh_2248226175642056_command_hhfq7zhxx =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_38sysaxdq,
      event: 'command',
      when: inst_evh_2248226175642056_command_hhfq7zhxx_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_hhfq7zhxx],
    event: 'command',
  });

  const actions = {
    inst_evh_7315092382398562_reload_o7hk2aqp9,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_xq8h3bw2m,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_command_hhfq7zhxx,
    inst_evh_2248226175642056_command,
  };

  const evh_7315092382398562_reload_o7hk2aqp9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_o7hk2aqp9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_xq8h3bw2m = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_xq8h3bw2m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_hhfq7zhxx = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_hhfq7zhxx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      name="payments"
      :headerTitle="pgettext('PaymentsView', 'Payments')"
      :items="ds"
      checkbox-row-selection
      :showColumnPicker="b_2248226175642056_showColumnPicker"
      header-show-reload-button
      footer-totals
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'payments')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <slot> </slot>
    </ItpDataGrid>
  </ItpUsercontrol>
</template>
